import Image from 'next/image';
import Link from 'next/link';

import {
  faFacebookF,
  faInstagram,
  faLinkedinIn,
  faTelegramPlane,
  faXTwitter,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import { faLocationDot, faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import { EVENT_ATTRIBUTE, EVENT_NAME, useMoengageEventTracker } from '../../hooks/useMoengage';

import chunks from '../../utils/chunks';

import styles from './Footer.module.scss';

const footerLinks = [
  {
    title: 'UPSC Exam Courses',
    href: '/upsc-exam-courses',
    links: [
      { label: 'UPSC Civil Services Exam', href: 'https://www.studyiq.com/articles/upsc-civil-services-exam/' },
      { label: 'UPSC EPFO PA Exam', href: 'https://www.studyiq.com/articles/upsc-epfo-pa-recruitment/' },
      { label: 'UPSC CAPF AC Exam', href: 'https://www.studyiq.com/articles/upsc-capf-ac-notification/' },
      { label: 'UPSC IES/ISS Exam', href: 'https://www.studyiq.com/articles/upsc-ies-iss-exam/' },
      { label: 'UPSC IFS Exam', href: 'https://www.studyiq.com/articles/upsc-ifs-exam/' },
    ],
  },
  {
    title: 'Judiciary Exam Courses',
    href: '/judiciary-exam-courses',
    links: [
      {
        label: 'All State Judiciary Services Exam',
        href: 'https://www.studyiq.com/articles/state-judicial-service-exams/',
      },
      { label: 'All State APO Exam', href: 'https://www.studyiq.com/articles/all-state-apo-exam/' },
      { label: 'AIBE Exam', href: 'https://www.studyiq.com/articles/aibe-application-form/' },
    ],
  },
  {
    title: 'STATE PSC Courses',
    href: '/state-psc-courses',
    links: [
      { label: 'UPPSC Online Coaching', href: 'https://www.studyiq.com/uppsc-online-coaching' },
      { label: 'Maharashtra PSC Coaching', href: null },
      { label: 'Bihar PSC Coaching', href: null },
      { label: 'Jammu Kashmir PSC Coaching', href: null },
      { label: 'Madhya Pradesh PSC Coaching', href: 'https://www.studyiq.com/articles/mppsc-exam/' },
      { label: 'West Bengal PSC Coaching', href: null },
      { label: 'Odisha PSC Coaching', href: null },
      { label: 'Assam PSC Coaching', href: null },
      { label: 'Haryana PSC Coaching', href: null },
      { label: 'Punjab PSC Coaching', href: null },
    ],
  },
  {
    title: 'UPSC Syllabus',
    href: '/upsc-syllabus',
    links: [
      { label: 'UPSC Prelims Syllabus', href: 'https://www.studyiq.com/articles/upsc-prelims-syllabus/' },
      { label: 'UPSC Mains Syllabus', href: 'https://www.studyiq.com/articles/upsc-mains-syllabus/' },
      {
        label: 'UPSC EPFO PA Syllabus',
        href: 'https://www.studyiq.com/articles/upsc-epfo-personal-assistant-syllabus/',
      },
      { label: 'UPSC CAPF AC Syllabus', href: 'https://www.studyiq.com/articles/upsc-capf-syllabus/' },
      { label: 'UPSC IES/ISS Syllabus', href: 'https://www.studyiq.com/articles/upsc-ies-iss-syllabus/' },
      { label: 'UPSC IFS Syllabus', href: 'https://www.studyiq.com/articles/upsc-ifs-syllabus/' },
    ],
  },
  {
    title: 'Important UPSC PDFs',
    href: '/upsc-pdfs',
    links: [
      {
        label: 'UPSC Monthly Magazine (Hindi)',
        href: 'https://www.studyiq.com/articles/ca-category/upsc-monthly-magazine-hindi/',
      },
      {
        label: 'UPSC Monthly Magazine (English)',
        href: 'https://www.studyiq.com/articles/ca-category/upsc-monthly-magazine-english/',
      },
      { label: 'PSIR Current Affairs', href: 'https://www.studyiq.com/articles/ca-category/psir-current-affairs/' },
      {
        label: 'UPSC Daily Current Affairs PDF (English)',
        href: 'https://www.studyiq.com/articles/ca-category/upsc-daily-current-affairs-pdf-in-english/',
      },
      {
        label: 'UPSC Daily Current Affairs PDF (Hindi)',
        href: 'https://www.studyiq.com/articles/ca-category/upsc-daily-current-affairs-pdf-in-hindi/',
      },
    ],
  },
  {
    title: 'Optional Subjects',
    href: '/optional-subjects',
    links: [
      { label: 'PSIR Syllabus', href: 'https://www.studyiq.com/articles/upsc-political-science-syllabus/' },
      { label: 'Sociology Syllabus', href: 'https://www.studyiq.com/articles/upsc-sociology-syllabus/' },
      { label: 'Geography Syllabus', href: 'https://www.studyiq.com/articles/upsc-geography-syllabus/' },
      { label: 'History Syllabus', href: 'https://www.studyiq.com/articles/upsc-history-syllabus/' },
      {
        label: 'Public Administration Syllabus',
        href: 'https://www.studyiq.com/articles/upsc-public-administration-syllabus/',
      },
      { label: 'Anthropology Syllabus', href: 'https://www.studyiq.com/articles/upsc-anthropology-syllabus/' },
      { label: 'Hindi Sahitya Syllabus', href: 'https://www.studyiq.com/articles/upsc-hindi-literature-syllabus/' },
      { label: 'Philosophy Syllabus', href: 'https://www.studyiq.com/articles/upsc-philosophy-syllabus/' },
      { label: 'Mathematics Syllabus', href: 'https://www.studyiq.com/articles/upsc-mathematics-syllabus/' },
      { label: 'Economics Syllabus', href: 'https://www.studyiq.com/articles/upsc-economics-syllabus/' },
    ],
  },
  {
    title: 'State Judiciary Syllabus',
    href: '/state-judiciary-syllabus',
    links: [
      { label: 'Uttar Pradesh Judiciary Syllabus', href: null },
      {
        label: 'Uttarakhand Judiciary Syllabus',
        href: null,
      },
      {
        label: 'Maharashtra Judiciary Syllabus',
        href: null,
      },
      { label: 'Bihar Judiciary Syllabus', href: 'https://www.studyiq.com/articles/bihar-judiciary-syllabus/' },
      {
        label: 'Himachal Pradesh Judiciary Syllabus',
        href: null,
      },
      {
        label: 'Gujarat Judiciary Syllabus',
        href: 'https://www.studyiq.com/articles/gujarat-judiciary-syllabus-and-exam-pattern/',
      },
      { label: 'Delhi Judiciary Syllabus', href: 'https://www.studyiq.com/articles/delhi-judiciary-syllabus/' },
      { label: 'Punjab Judiciary Syllabus', href: null },
      {
        label: 'Chhattisgarh Judiciary Syllabus',
        href: null,
      },
      { label: 'Haryana Judiciary Syllabus', href: 'https://www.studyiq.com/articles/hpsc-judiciary-syllabus/' },
      {
        label: 'Madhya Pradesh Judiciary Syllabus',
        href: 'https://www.studyiq.com/articles/madhya-pradesh-judiciary-syllabus/',
      },
      { label: 'Jharkhand Judiciary Syllabus', href: 'https://www.studyiq.com/articles/jharkhand-judiciary-syllabus/' },
      { label: 'Rajasthan Judiciary Syllabus', href: 'https://www.studyiq.com/articles/rajasthan-judiciary-syllabus/' },
      {
        label: 'West Bengal Judiciary Syllabus',
        href: null,
      },
      {
        label: 'Odisha Judiciary Syllabus',
        href: 'https://www.studyiq.com/articles/odisha-judicial-service-syllabus/',
      },
      {
        label: 'Jammu & Kashmir Judiciary Syllabus',
        href: null,
      },
    ],
  },
  {
    title: 'STATE PSC Syllabus',
    href: '/state-psc-syllabus',
    links: [
      { label: 'UPPSC Syllabus', href: 'https://www.studyiq.com/articles/uppsc-syllabus/' },
      { label: 'UKPSC Syllabus', href: 'https://www.studyiq.com/articles/ukpsc-syllabus/' },
      { label: 'MPSC Syllabus', href: 'https://www.studyiq.com/articles/mpsc-syllabus/' },
      { label: 'BPSC Syllabus', href: 'https://www.studyiq.com/articles/bpsc-syllabus/' },
      { label: 'HPPSC Syllabus', href: 'https://www.studyiq.com/articles/hppsc-syllabus/' },
      { label: 'GPSC Syllabus', href: 'https://www.studyiq.com/articles/gpsc-syllabus/' },
      { label: 'JKPSC Syllabus', href: 'https://www.studyiq.com/articles/jkpsc-syllabus/' },
      { label: 'PPSC Syllabus', href: 'https://www.studyiq.com/articles/ppsc-syllabus/' },
      { label: 'CGPSC Syllabus', href: 'https://www.studyiq.com/articles/cgpsc-syllabus/' },
      { label: 'HPSC Syllabus', href: 'https://www.studyiq.com/articles/hpsc-syllabus/' },
      { label: 'MPPSC Syllabus', href: 'https://www.studyiq.com/articles/mppsc-syllabus/' },
      { label: 'JPSC Syllabus', href: 'https://www.studyiq.com/articles/jpsc-syllabus/' },
      { label: 'RPSC RAS Syllabus', href: 'https://www.studyiq.com/articles/rpsc-ras-syllabus/' },
      { label: 'WBPSC Syllabus', href: 'https://www.studyiq.com/articles/wbpsc-syllabus/' },
      { label: 'OPSC OCS Syllabus', href: 'https://www.studyiq.com/articles/opsc-ocs-syllabus/' },
      { label: 'APSC CCE Syllabus', href: 'https://www.studyiq.com/articles/apsc-cce-syllabus/' },
    ],
  },
  {
    title: 'UPSC PYQs',
    href: '/upsc-pyqs',
    links: [
      { label: 'UPSC Essay PYQs', href: 'https://www.studyiq.com/articles/ca-category/upsc-essay-pyqs/' },
      {
        label: 'UPSC History Optional PYQs',
        href: 'https://www.studyiq.com/articles/ca-category/upsc-history-optional-pyqs/',
      },
      {
        label: 'UPSC Mains General Studies PYQs',
        href: 'https://www.studyiq.com/articles/ca-category/upsc-mains-general-studies-pyqs/',
      },
      { label: 'UPSC Prelims PYQs', href: 'https://www.studyiq.com/articles/ca-category/upsc-prelims-pyqs/' },
    ],
  },
  {
    title: 'Judiciary PYQs',
    href: '/judiciary-pyqs',
    links: [
      { label: 'Assam Judiciary PYQs', href: 'https://www.studyiq.com/articles/jt-category/assam-judiciary-pyqs/' },
      {
        label: 'Himachal Pradesh Judiciary PYQs',
        href: 'https://www.studyiq.com/articles/jt-category/himachal-pradesh-judiciary-pyqs/',
      },
      { label: 'Punjab Judiciary PYQs', href: 'https://www.studyiq.com/articles/jt-category/punjab-judiciary-pyqs/' },
      {
        label: 'Karnataka Judiciary PYQs',
        href: 'https://www.studyiq.com/articles/jt-category/karnataka-judiciary-pyqs/',
      },
    ],
  },
  {
    title: 'State PCS Solved PYQs',
    href: '/state-pcs-pyqs',
    links: [
      { label: 'MPPSC Prelims 2025', href: 'https://www.studyiq.com/articles/mppsc-prelims-2025-solved-paper/' },
      {
        label: 'JKPSC Prelims 2025',
        href: 'https://www.studyiq.com/articles/jkpsc-prelims-2025-solved-question-paper/',
      },
      { label: 'CGPSC Prelims 2025', href: 'https://www.studyiq.com/articles/cgpsc-prelims-solved-paper/' },
      { label: 'UPPSC Prelims 2024', href: 'https://www.studyiq.com/articles/uppsc-solved-question-paper-2024/' },
      { label: 'BPSC Prelims 2024', href: 'https://www.studyiq.com/articles/70th-bpsc-question-paper-solution-2025/' },
    ],
  },
  {
    title: 'Support & Contact',
    href: '/',
    links: [
      { label: 'Terms & Conditions', href: '/terms-and-conditions' },
      { label: 'Pedagogy', href: 'https://dl.studyiq.com/GgCq' },
      { label: 'Return & Refund Policy', href: '/return-policy' },
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Email Us', href: 'mailto:[[EMAIL]]' },
      { label: 'Contact Us', href: '/contact' },
    ],
  },
];

const companyLinks = [
  {
    title: 'Company',
    href: '/',
    links: [
      { label: 'Home', href: '/' },
      { label: 'About Us', href: '/about-us' },
      { label: 'Blog', href: 'https://www.studyiq.com/articles' },
      { label: 'Faculty Excellence Program', href: 'https://www.studyiq.com/lp/teacher-excellence-program.html' },
      { label: 'StudyIQ Youtube', href: 'https://www.youtube.com/@StudyIQEducationLtd' },
      { label: 'Career with Us', href: '/career' },
      { label: 'Free Learning', href: '/free-courses' },
    ],
  },
  {
    title: 'Our Centres',
    href: '/',
    links: [
      { label: 'UPSC Coaching in Delhi ORN', href: 'https://www.studyiq.com/best-upsc-coaching-in-delhi' },
      { label: 'UPSC Coaching in Patna', href: 'https://www.studyiq.com/best-upsc-coaching-in-patna' },
      { label: 'UPSC Coaching in Prayagraj', href: 'https://www.studyiq.com/best-upsc-coaching-in-prayagraj' },
      { label: 'UPSC Coaching in Lucknow', href: 'https://www.studyiq.com/best-upsc-coaching-in-lucknow' },
      { label: 'UPSC Coaching in Indore', href: 'https://www.studyiq.com/best-upsc-coaching-in-indore' },
      { label: 'UPSC Coaching in Guwahati', href: 'https://www.studyiq.com/best-upsc-coaching-in-guwahati' },
    ],
  },
  {
    title: 'Popular Courses',
    href: '/popular-courses',
    links: [
      { label: 'Bestselling Courses', href: 'https://www.studyiq.com/view-all/bestselling-courses' },
      { label: 'Just Launched', href: 'https://www.studyiq.com/view-all/just-launched' },
      { label: 'Dr Gaurav Garg Courses', href: 'https://www.studyiq.com/view-all/dr-gaurav-garg-courses' },
      { label: 'Dr Vipan Goyal Courses', href: 'https://www.studyiq.com/view-all/dr-vipan-goyal-courses' },
      { label: 'Best UPSC Mentorship Program', href: 'https://www.studyiq.com/view-all/best-upsc-mentorship-program' },
    ],
  },
];

function SocialAccountsFooter() {
  const trackMoengageEvent = useMoengageEventTracker();

  const handleOnSocialShareClick = (platform) => () => {
    trackMoengageEvent(EVENT_NAME.FOLLOW_US_CLICKED, {
      [EVENT_ATTRIBUTE.SOCIAL_MEDIA_PLATFORM]: platform,
    });
  };

  return (
    <div className={styles.socialShareContainerWrapper}>
      <div className={styles.socialShareContainer}>
        <span className={styles.socialShareTitle}>Follow Us</span>

        <ul className={styles.socialShareItems}>
          <li>
            <Link
              href="https://www.facebook.com/studyiq/"
              className={styles.facebook}
              title="Facebook"
              onClick={handleOnSocialShareClick('facebook')}
            >
              <FontAwesomeIcon icon={faFacebookF} />
            </Link>
          </li>
          <li>
            <Link
              href="https://www.linkedin.com/company/studyiq"
              className={styles.linkedin}
              title="LinkedIn"
              onClick={handleOnSocialShareClick('linkedin')}
            >
              <FontAwesomeIcon icon={faLinkedinIn} />
            </Link>
          </li>
          <li>
            <Link
              href="https://twitter.com/studyiq"
              className={styles.twitter}
              title="Twitter"
              onClick={handleOnSocialShareClick('twitter')}
            >
              <FontAwesomeIcon icon={faXTwitter} />
            </Link>
          </li>
          <li>
            <Link
              href="https://t.me/Studyiqeducation"
              className={styles.telegram}
              title="Telegram"
              onClick={handleOnSocialShareClick('telegram')}
            >
              <FontAwesomeIcon icon={faTelegramPlane} />
            </Link>
          </li>
          <li>
            <Link
              href="https://www.youtube.com/channel/UCrC8mOqJQpoB7NuIMKIS6rQ"
              className={styles.youtube}
              title="Youtube"
              onClick={handleOnSocialShareClick('youtube')}
            >
              <FontAwesomeIcon icon={faYoutube} />
            </Link>
          </li>
          <li>
            <Link
              href="https://www.instagram.com/studyiq.education/"
              className={styles.instagram}
              title="Instagram"
              onClick={handleOnSocialShareClick('instagram')}
            >
              <FontAwesomeIcon icon={faInstagram} />
            </Link>
          </li>
        </ul>
      </div>
    </div>
  );
}

function FooterMenuLink({ href, children, ...props }) {
  // Checking for external links
  let Anchor = href?.startsWith('http') ? 'a' : Link;
  // If no href is provided then it will be a span
  Anchor = href ? Anchor : 'span';

  return (
    <Anchor href={href} {...props}>
      {children}
    </Anchor>
  );
}

function Footer({ footerData }) {
  const trackMoengageEvent = useMoengageEventTracker();

  const replaceLinkTemplateWithDataKey =
    (link, dataObj) => link?.replace(/\[\[(\w+)\]\]/, (_, itemKey) => dataObj?.[itemKey] || '');

  const handleOnFooterClick = (type, value) => () => {
    sessionStorage.removeItem('scrollToTryNew');
    trackMoengageEvent('footer_clicked', {
      Type: type,
      value,
    });
  };

  return (
    <footer id="footer" className={styles.footerWrapper}>
      <div className={styles.footerTopContainer}>
        <div className={styles.companyContainer}>
          <div className={styles.companyLogoWrapper}>
            <Image src="/studyiq.png" height={120} width={120} className={styles.companyLogo} />
          </div>

          <div className={styles.companyDetails}>
            <div className={styles.companyAddress}>
              <FontAwesomeIcon icon={faLocationDot} />

              {/* eslint-disable-next-line max-len */}
              <span>
                {footerData?.constantWeb?.PATNER_US}
              </span>
            </div>
            <div className={styles.companyContact}>
              <FontAwesomeIcon icon={faPhone} />
              <span>
                For any help you can call us at
                <br />
                {footerData?.constantWeb?.CALL_US}
                <br />
                ({footerData?.constantWeb?.WORKING_HOURS})
              </span>
            </div>

            <div className={styles.mobileAppsWrapper}>
              <Link
                href="https://play.google.com/store/apps/details?id=com.studyiq.android&hl=en_IN&gl=US"
                onClick={() => trackMoengageEvent(EVENT_NAME.GOOGLE_PLAY_CLICKED)}
              >
                <Image src="/images/app-platform/playstore.png" alt="StudyIQ Android App" width={95} height={28.79} />
              </Link>
              <Link
                href="https://apps.apple.com/in/app/adda247/id6451410946"
                onClick={() => trackMoengageEvent(EVENT_NAME.APP_STORE_CLICKED)}
              >
                <Image src="/images/app-platform/appstore.png" alt="StudyIQ iOS App" width={95} height={28.72} />
              </Link>
            </div>
          </div>
        </div>

        <SocialAccountsFooter />

        <div className={styles.quickLinksWrapper}>
          {companyLinks.map((companyLink) => (
            <div key={companyLink.title} className={styles.menuWrapper}>
              <span className={styles.menuHeader}>{companyLink.title}</span>

              <ul className={styles.menuList}>
                {companyLink.links.map((link) => (
                  <li key={link.href}>
                    <FooterMenuLink
                      href={link.href}
                      onClick={handleOnFooterClick(companyLink.title, link.label)}
                    >
                      {link.label}
                    </FooterMenuLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.footerMiddleContainer}>
        <SocialAccountsFooter />
      </div>

      <div id="footer-links" className={styles.footerBottomContainer}>
        {footerLinks.map((section) => {
          if (section.links.length <= 6) {
            return (
              <div key={section.title} className={styles.menuWrapper}>
                <span className={styles.menuHeader}>
                  {section.title}
                </span>
                <ul className={styles.menuList}>
                  {section.links.map((link) => (
                    <li key={`${section.title}-${link.label}`}>
                      <FooterMenuLink
                        href={replaceLinkTemplateWithDataKey(link.href, footerData?.constantWeb)}
                        onClick={handleOnFooterClick(section.title, link.label)}
                      >
                        {link.label}
                      </FooterMenuLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          }

          const sections = [...chunks(section.links, Math.ceil(section.links.length / 2))];

          return sections.map((sectionChunk) => (
            <div
              key={`${section.title}-${sectionChunk[0].label}`}
              className={classNames(styles.menuWrapper, sectionChunk !== sections[0] && styles.withoutHeader)}
            >
              {sectionChunk === sections[0] && (
                <span className={styles.menuHeader}>
                  {section.title}
                </span>
              )}
              <ul className={styles.menuList}>
                {sectionChunk.map((link) => (
                  <li key={`${section.title}-${link.label}`}>
                    <FooterMenuLink
                      href={replaceLinkTemplateWithDataKey(link.href, footerData?.constantWeb)}
                      onClick={handleOnFooterClick(section.title, link.label)}
                    >
                      {link.label}
                    </FooterMenuLink>
                  </li>
                ))}
              </ul>
            </div>
          ));
        })}
      </div>
    </footer>
  );
}

export default Footer;
